import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import VisibilityIcon from '@mui/icons-material/Visibility'
import DeleteIcon from '@mui/icons-material/Delete'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import {
  accountService,
  vehicleService,
  scoreService,
  vehicleRatingService,
  VehicleDto,
  VehicleType,
  AccountDto,
} from '../api/adminServices'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { exportCsv } from '../utils/exportCsv'
import VerificationReviewDialog from '../components/VerificationReviewDialog'
import ConfirmDialog from '../components/ConfirmDialog'
import { useToast } from '../components/useToast'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import { PropertyView, PropertySection, PropertyField } from '../components/PropertyView'
import SubjectDocuments from '../components/SubjectDocuments'

const PAGE_SIZE = 20
const KYC_STATUSES = ['PENDING', 'VERIFIED', 'REJECTED', 'EXPIRED', 'SUSPENDED']
const VEHICLE_TYPES: VehicleType[] = [
  'VAN',
  'PICKUP_TRUCK',
  'SMALL_TRUCK_3_5_TON',
  'MEDIUM_TRUCK_7_5_TON',
  'LARGE_TRUCK_12_TON',
  'SEMI_TRAILER',
]

function kycChipColor(s?: string): 'success' | 'warning' | 'error' | 'default' {
  if (s === 'VERIFIED') return 'success'
  if (s === 'REJECTED' || s === 'EXPIRED') return 'error'
  if (s === 'PENDING' || s === 'SUSPENDED') return 'warning'
  return 'default'
}

function daysUntil(dateStr?: string): number | null {
  if (!dateStr) return null
  const ms = new Date(dateStr).getTime() - Date.now()
  return Math.floor(ms / 86400000)
}

function expiryChip(dateStr?: string) {
  const d = daysUntil(dateStr)
  if (d == null) return <Typography variant="caption" color="text.secondary">—</Typography>
  const label = new Date(dateStr as string).toLocaleDateString()
  if (d < 0) return <Chip label={`${label} (expired)`} color="error" size="small" />
  if (d <= 30) return <Chip label={`${label} (${d}d)`} color="warning" size="small" />
  return <Chip label={label} color="default" size="small" variant="outlined" />
}

function formatVehicleDimensions(
  lengthM?: number,
  widthM?: number,
  heightM?: number
): string | null {
  if (lengthM == null && widthM == null && heightM == null) return null
  return [lengthM, widthM, heightM]
    .map((n) => (n != null ? `${n}m` : '?'))
    .join(' × ')
}

function capabilityChips(v: VehicleDto) {
  const tags: string[] = []
  if (v.boxTruck) tags.push('Box')
  if (v.curtainsider) tags.push('Curtainsider')
  if (v.flatbed) tags.push('Flatbed')
  if (v.hasHazmat) tags.push('Hazmat')
  if (v.hasRefrigeration) tags.push('Refrigerated')
  if (v.hasRamp) tags.push('Ramp')
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
      {tags.map((t) => (
        <Chip key={t} label={t} size="small" variant="outlined" />
      ))}
      {tags.length === 0 && (
        <Typography variant="caption" color="text.secondary">—</Typography>
      )}
    </Stack>
  )
}

export default function VehiclesPage() {
  const [page, setPage] = useState(0)
  const [accountFilter, setAccountFilter] = useState<string>('')
  const [kycFilter, setKycFilter] = useState<string>('')
  const [typeFilter, setTypeFilter] = useState<string>('')
  const [expiryFilter, setExpiryFilter] = useState<'ALL' | 'EXPIRED' | 'SOON' | 'OK'>(
    'ALL'
  )
  const [search, setSearch] = useState('')

  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null)

  const [scoreDialogOpen, setScoreDialogOpen] = useState(false)
  const [scoreAdjustment, setScoreAdjustment] = useState(0)
  const [scoreReason, setScoreReason] = useState('')

  const [kycDialogOpen, setKycDialogOpen] = useState(false)
  const [kycVerificationId, setKycVerificationId] = useState<string | undefined>(undefined)
  const [kycInitiateParams, setKycInitiateParams] = useState<
    { subjectType: 'VEHICLE'; subjectId: string; accountId: string } | undefined
  >(undefined)

  const [deleteConfirm, setDeleteConfirm] = useState<VehicleDto | null>(null)
  const [resetRatingTarget, setResetRatingTarget] = useState<VehicleDto | null>(null)

  const toast = useToast()
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()

  // Cross-account paginated list
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-vehicles', page, accountFilter],
    queryFn: () => vehicleService.getAll(page, PAGE_SIZE, accountFilter || undefined),
  })

  // Accounts for dropdown
  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-filter'],
    queryFn: () => accountService.getAll(0, 200),
  })
  const accounts: AccountDto[] = useMemo(
    () => accountsData?.content ?? accountsData ?? [],
    [accountsData]
  )
  const accountById = useMemo(() => {
    const m = new Map<string, AccountDto>()
    accounts.forEach((a) => m.set(a.id, a))
    return m
  }, [accounts])

  const { data: selectedVehicle } = useQuery({
    queryKey: ['vehicle', selectedVehicleId],
    queryFn: () => vehicleService.getById(selectedVehicleId as string),
    enabled: viewDialogOpen && !!selectedVehicleId,
  })

  const deleteMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleService.delete(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-vehicles'] })
      toast.success('Vehicle deleted')
      setDeleteConfirm(null)
    },
    onError: (e) => toast.errorFrom(e, 'Failed to delete vehicle'),
  })

  const activateMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleService.activate(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['vehicle', selectedVehicleId] })
      toast.success('Vehicle activated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to activate vehicle'),
  })

  const deactivateMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleService.deactivate(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['vehicle', selectedVehicleId] })
      toast.success('Vehicle deactivated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to deactivate vehicle'),
  })

  const resetRatingMutation = useMutation({
    mutationFn: (vehicleId: string) => vehicleRatingService.resetRating(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['vehicle', selectedVehicleId] })
      toast.success('Rating reset')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to reset rating'),
  })

  const adjustScoreMutation = useMutation({
    mutationFn: ({
      vehicleId,
      adjustment,
      reason,
    }: {
      vehicleId: string
      adjustment: number
      reason: string
    }) =>
      scoreService.adjustScore('VEHICLE', vehicleId, { adjustment, reason }),
    onSuccess: () => {
      toast.success('Score adjusted')
      setScoreDialogOpen(false)
      setScoreAdjustment(0)
      setScoreReason('')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to adjust score'),
  })

  const vehicles: VehicleDto[] = useMemo(() => data?.content ?? [], [data])
  const totalPages: number = data?.totalPages ?? 0

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      if (kycFilter && v.kycVerificationStatus !== kycFilter) return false
      if (typeFilter && v.type !== typeFilter) return false
      if (search) {
        const q = search.toLowerCase()
        const hay = [v.plateNo, v.id, v.maker, v.model, v.homeBaseAddress]
        if (!hay.some((x) => x?.toLowerCase().includes(q))) return false
      }
      if (expiryFilter !== 'ALL') {
        const d = daysUntil(v.licenseExpiry)
        if (expiryFilter === 'EXPIRED' && (d == null || d >= 0)) return false
        if (expiryFilter === 'SOON' && (d == null || d < 0 || d > 30)) return false
        if (expiryFilter === 'OK' && (d == null || d <= 30)) return false
      }
      return true
    })
  }, [vehicles, kycFilter, typeFilter, search, expiryFilter])

  const openVehicle = (id: string) => {
    setSelectedVehicleId(id)
    setViewDialogOpen(true)
  }

  const handleExport = () => {
    exportCsv<VehicleDto>(`vehicles-${new Date().toISOString().slice(0, 10)}`,
      [
        { header: 'ID', field: 'id' },
        { header: 'Plate', field: 'plateNo' },
        { header: 'Maker', field: 'maker' },
        { header: 'Model', field: 'model' },
        { header: 'Year', field: 'year' },
        { header: 'Type', field: 'type' },
        { header: 'Capacity (kg)', field: 'capacityKg' },
        { header: 'L (m)', field: 'lengthM' },
        { header: 'W (m)', field: 'widthM' },
        { header: 'H (m)', field: 'heightM' },
        { header: 'License Expiry', field: 'licenseExpiry' },
        { header: 'Last Test', field: 'lastTestDate' },
        { header: 'KYC', field: 'kycVerificationStatus' },
        { header: 'Account', field: 'accountId' },
        { header: 'Rating', field: 'rating' },
        { header: 'Created', field: 'createdAt' },
      ],
      filteredVehicles)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mb={2}
        spacing={2}
      >
        <Stack direction="row" spacing={1}>
          <Tooltip title="Export current view to CSV">
            <IconButton onClick={handleExport}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-vehicles'] })}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            select
            size="small"
            label="Account"
            value={accountFilter}
            onChange={(e) => {
              setAccountFilter(e.target.value)
              setPage(0)
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All accounts</MenuItem>
            {accounts
              .filter((a) => a.type === 'FREIGHTER')
              .map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.businessName || a.id.substring(0, 8)}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All types</MenuItem>
            {VEHICLE_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {getLocalizedLabel('vehicleType', t) || t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="KYC"
            value={kycFilter}
            onChange={(e) => setKycFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            {KYC_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {getLocalizedLabel('kycVerificationStatus', s) || s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="License"
            value={expiryFilter}
            onChange={(e) => setExpiryFilter(e.target.value as any)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="EXPIRED">Expired</MenuItem>
            <MenuItem value="SOON">≤ 30 days</MenuItem>
            <MenuItem value="OK">More than 30d</MenuItem>
          </TextField>
          <TextField
            size="small"
            placeholder="Search id / plate / maker / model / home base"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Paper>

      {isLoading ? (
        <Paper sx={{ flex: 1, minHeight: 0 }}><TableSkeleton rows={8} columns={10} /></Paper>
      ) : isError ? (
        <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorCard
            title="Failed to load vehicles"
            message={(error as Error)?.message}
            onRetry={() => refetch()}
          />
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table sx={{ height: filteredVehicles.length === 0 ? '100%' : undefined }}>
            <TableHead>
              <TableRow>
                <ResizableTableCell>Plate</ResizableTableCell>
                <ResizableTableCell>Type</ResizableTableCell>
                <ResizableTableCell>Maker / Model / Year</ResizableTableCell>
                <ResizableTableCell>Capacity</ResizableTableCell>
                <ResizableTableCell>Dimensions (L×W×H)</ResizableTableCell>
                <ResizableTableCell>License Expiry</ResizableTableCell>
                <ResizableTableCell>KYC</ResizableTableCell>
                <ResizableTableCell>Account</ResizableTableCell>
                <ResizableTableCell>Rating</ResizableTableCell>
                <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVehicles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                    <EmptyState
                      title="No vehicles match the current filters"
                      description="Adjust the account, type, KYC, expiry, or search filters above."
                    />
                  </TableCell>
                </TableRow>
              )}
              {filteredVehicles.map((v) => {
                const account = v.accountId ? accountById.get(v.accountId) : undefined
                return (
                  <TableRow
                    key={v.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openVehicle(v.id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {v.plateNo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {v.type
                        ? getLocalizedLabel('vehicleType', v.type) || v.type
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {[v.maker, v.model, v.year].filter(Boolean).join(' / ') || '—'}
                    </TableCell>
                    <TableCell>
                      {v.capacityKg ? `${v.capacityKg.toLocaleString()} kg` : '—'}
                    </TableCell>
                    <TableCell>
                      {formatVehicleDimensions(v.lengthM, v.widthM, v.heightM) ?? '—'}
                    </TableCell>
                    <TableCell>{expiryChip(v.licenseExpiry)}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          getLocalizedLabel('kycVerificationStatus', v.kycVerificationStatus || '') ||
                          v.kycVerificationStatus ||
                          '—'
                        }
                        color={kycChipColor(v.kycVerificationStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={v.accountId || ''}>
                        <span>{account?.businessName || (v.accountId?.substring(0, 8) + '…' || '—')}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {typeof v.rating === 'number' && v.rating > 0 ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Rating value={Number(v.rating)} readOnly size="small" precision={0.5} />
                          <Typography variant="caption" color="text.secondary">
                            ({v.ratingCount || 0})
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()} sx={stickyActionsBodySx}>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => openVehicle(v.id)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={v.isActive !== false ? 'Deactivate' : 'Activate'}>
                          <IconButton
                            size="small"
                            color={v.isActive !== false ? 'warning' : 'success'}
                            disabled={activateMutation.isPending || deactivateMutation.isPending}
                            onClick={() =>
                              v.isActive !== false
                                ? deactivateMutation.mutate(v.id)
                                : activateMutation.mutate(v.id)
                            }
                          >
                            <PowerSettingsNewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteConfirm(v)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page + 1}
            onChange={(_e, v) => setPage(v - 1)}
            color="primary"
          />
        </Box>
      )}

      {/* Vehicle detail dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setSelectedVehicleId(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Vehicle Details {selectedVehicle?.plateNo ? ` — ${selectedVehicle.plateNo}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {selectedVehicle && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Chip
                  label={
                    getLocalizedLabel('kycVerificationStatus', selectedVehicle.kycVerificationStatus || '') ||
                    selectedVehicle.kycVerificationStatus ||
                    '—'
                  }
                  color={kycChipColor(selectedVehicle.kycVerificationStatus)}
                  size="small"
                />
                {selectedVehicle.type && (
                  <Chip
                    label={getLocalizedLabel('vehicleType', selectedVehicle.type) || selectedVehicle.type}
                    size="small"
                    variant="outlined"
                  />
                )}
                {selectedVehicle.rating != null && selectedVehicle.rating > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Rating value={Number(selectedVehicle.rating)} readOnly size="small" precision={0.5} />
                    <Typography variant="caption" color="text.secondary">
                      ({selectedVehicle.ratingCount || 0})
                    </Typography>
                  </Stack>
                )}
              </Stack>

              <Divider />

              <PropertyView>
                <PropertySection title="Identity">
                  <PropertyField label="Vehicle ID" value={selectedVehicle.id} mono copyable />
                  <PropertyField label="Account ID" value={selectedVehicle.accountId} mono copyable />
                  {selectedVehicle.defaultDriverId && (
                    <PropertyField
                      label="Default Driver ID"
                      value={selectedVehicle.defaultDriverId}
                      mono
                      copyable
                      full
                    />
                  )}
                </PropertySection>
                <PropertySection title="Specs" columns={3}>
                  <PropertyField label="Plate" value={selectedVehicle.plateNo} />
                  <PropertyField label="Maker" value={selectedVehicle.maker} />
                  <PropertyField label="Model" value={selectedVehicle.model} />
                  <PropertyField label="Year" value={selectedVehicle.year} />
                  <PropertyField
                    label="Capacity"
                    value={
                      selectedVehicle.capacityKg
                        ? `${selectedVehicle.capacityKg.toLocaleString()} kg`
                        : null
                    }
                  />
                  <PropertyField
                    label="Dimensions (L × W × H)"
                    value={formatVehicleDimensions(
                      selectedVehicle.lengthM,
                      selectedVehicle.widthM,
                      selectedVehicle.heightM
                    )}
                  />
                  <PropertyField
                    label="Fuel (L/100km)"
                    value={selectedVehicle.fuelConsumptionPer100Km}
                  />
                  <PropertyField
                    label="Loading Side"
                    value={
                      selectedVehicle.loadingSide
                        ? getLocalizedLabel('loadingSide', selectedVehicle.loadingSide) ||
                          selectedVehicle.loadingSide
                        : null
                    }
                  />
                </PropertySection>
                <PropertySection title="Compliance">
                  <PropertyField label="License Expiry">
                    {expiryChip(selectedVehicle.licenseExpiry)}
                  </PropertyField>
                  <PropertyField label="Last Test" value={selectedVehicle.lastTestDate} type="date" />
                </PropertySection>
                <PropertySection title="Operations">
                  <PropertyField label="Capabilities" full>
                    <Box>{capabilityChips(selectedVehicle)}</Box>
                  </PropertyField>
                  <PropertyField
                    label="Home Base"
                    value={
                      selectedVehicle.homeBaseAddress ||
                      (selectedVehicle.homeBaseLatitude && selectedVehicle.homeBaseLongitude
                        ? `${selectedVehicle.homeBaseLatitude}, ${selectedVehicle.homeBaseLongitude}`
                        : null)
                    }
                    full
                  />
                </PropertySection>
                <SubjectDocuments subjectType="VEHICLE" subjectId={selectedVehicle.id} />
              </PropertyView>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedVehicle && (
            <>
              <Button
                startIcon={<VerifiedUserIcon />}
                onClick={() => {
                  setKycVerificationId(undefined)
                  setKycInitiateParams({
                    subjectType: 'VEHICLE',
                    subjectId: selectedVehicle.id,
                    accountId: selectedVehicle.accountId || '',
                  })
                  setKycDialogOpen(true)
                }}
              >
                Review KYC
              </Button>
              <Button
                startIcon={<TrendingUpIcon />}
                onClick={() => {
                  setScoreAdjustment(0)
                  setScoreReason('')
                  setScoreDialogOpen(true)
                }}
              >
                Adjust Score
              </Button>
              <Button
                color="warning"
                startIcon={<RestartAltIcon />}
                onClick={() => setResetRatingTarget(selectedVehicle)}
                disabled={resetRatingMutation.isPending}
              >
                Reset Rating
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Score adjustment dialog */}
      <Dialog
        open={scoreDialogOpen}
        onClose={() => setScoreDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Adjust Vehicle Score</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              type="number"
              label="Adjustment (positive or negative) *"
              value={scoreAdjustment}
              onChange={(e) => setScoreAdjustment(parseInt(e.target.value, 10) || 0)}
              fullWidth
              size="small"
              inputProps={{ step: 1 }}
            />
            <TextField
              label="Reason *"
              value={scoreReason}
              onChange={(e) => setScoreReason(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !scoreAdjustment ||
              !scoreReason.trim() ||
              !selectedVehicleId ||
              adjustScoreMutation.isPending
            }
            onClick={() =>
              selectedVehicleId &&
              adjustScoreMutation.mutate({
                vehicleId: selectedVehicleId,
                adjustment: scoreAdjustment,
                reason: scoreReason.trim(),
              })
            }
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* KYC review dialog */}
      <VerificationReviewDialog
        open={kycDialogOpen}
        onClose={() => {
          setKycDialogOpen(false)
          setKycVerificationId(undefined)
          setKycInitiateParams(undefined)
        }}
        verificationId={kycVerificationId}
        initiate={kycInitiateParams}
        onActionComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['admin-vehicles'] })
          queryClient.invalidateQueries({ queryKey: ['vehicle', selectedVehicleId] })
        }}
      />

      {/* Delete confirmation (typed) */}
      <ConfirmDialog
        open={!!deleteConfirm}
        title="Delete vehicle?"
        description={
          deleteConfirm ? (
            <>
              You're about to delete vehicle <strong>{deleteConfirm.plateNo}</strong>. This
              cannot be undone.
            </>
          ) : (
            'This cannot be undone.'
          )
        }
        confirmPhrase={deleteConfirm?.plateNo || 'DELETE'}
        confirmHelper="Type the plate number to confirm."
        confirmLabel="Delete"
        severity="error"
        loading={deleteMutation.isPending}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
      />

      {/* Reset-rating confirmation (typed) */}
      <ConfirmDialog
        open={!!resetRatingTarget}
        title="Reset vehicle rating?"
        description={
          <>
            This sets the vehicle's <code>rating</code> to <code>null</code> and{' '}
            <code>ratingCount</code> to <code>0</code>. This cannot be undone.
          </>
        }
        confirmPhrase="RESET"
        confirmLabel="Reset Rating"
        severity="warning"
        loading={resetRatingMutation.isPending}
        onClose={() => setResetRatingTarget(null)}
        onConfirm={() => {
          if (resetRatingTarget) resetRatingMutation.mutate(resetRatingTarget.id)
          setResetRatingTarget(null)
        }}
      />
    </Box>
  )
}
